import { useState, useRef, useCallback } from "react";
import { Box, IconButton, InputBase, Typography, Alert } from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useTranslation } from "react-i18next";
import { EmojiPicker } from "./EmojiPicker";

const MAX_LENGTH = 5000;

export const MessageComposer = ({
  onSend,
  disabled,
  sending,
  contactLocked,
  isReadonly,
  moderationError,
  onClearModError,
}) => {
  const { t } = useTranslation("chat");
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  const submitRef = useRef(false);

  const handleSend = useCallback(async () => {
    if (submitRef.current) return; // prevent double submit
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > MAX_LENGTH || disabled || sending) return;

    submitRef.current = true;
    try {
      await onSend(trimmed);
      setText("");
      inputRef.current?.focus();
    } catch {
      // Error handled by parent
    } finally {
      submitRef.current = false;
    }
  }, [text, disabled, sending, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiSelect = useCallback((emoji) => {
    setText((prev) => prev + emoji);
    // Focus back on input after emoji selection
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  if (isReadonly) {
    return (
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid rgba(7,31,40,0.08)",
          textAlign: "center",
        }}
      >
        <Typography variant="caption" color="text.disabled">
          This conversation is read-only.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ borderTop: "1px solid rgba(7,31,40,0.08)" }}>
      {/* Safety notice when contact is locked */}
      {contactLocked && (
        <Box
          sx={{
            px: 2,
            py: 1,
            backgroundColor: "rgba(255,152,0,0.06)",
            borderBottom: "1px solid rgba(255,152,0,0.1)",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#e65100", fontSize: 11, lineHeight: 1.4 }}
          >
            🔒 {t("contact.safetyNotice")}
          </Typography>
        </Box>
      )}

      {/* Moderation error */}
      {moderationError && (
        <Alert
          severity="warning"
          onClose={onClearModError}
          sx={{
            borderRadius: 0,
            py: 0.25,
            "& .MuiAlert-message": { fontSize: 12 },
          }}
        >
          {moderationError}
        </Alert>
      )}

      {/* Input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          p: 1.5,
        }}
      >
        <EmojiPicker onSelect={handleEmojiSelect} disabled={disabled || sending} />
        <InputBase
          inputRef={inputRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (moderationError) onClearModError?.();
          }}
          onKeyDown={handleKeyDown}
          placeholder={t("typeMessage")}
          multiline
          maxRows={4}
          disabled={disabled || sending}
          inputProps={{
            "aria-label": t("typeMessage"),
            maxLength: MAX_LENGTH,
          }}
          sx={{
            flex: 1,
            backgroundColor: "rgba(7,31,40,0.03)",
            borderRadius: "12px",
            px: 2,
            py: 1,
            fontSize: 14,
            lineHeight: 1.5,
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!text.trim() || text.length > MAX_LENGTH || disabled || sending}
          aria-label={t("send")}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            width: 40,
            height: 40,
            "&:hover": { backgroundColor: "#c91906" },
            "&.Mui-disabled": {
              backgroundColor: "rgba(7,31,40,0.08)",
              color: "text.disabled",
            },
          }}
        >
          <SendRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Character count when near limit */}
      {text.length > MAX_LENGTH * 0.9 && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "right",
            px: 2,
            pb: 0.5,
            color: text.length > MAX_LENGTH ? "#d32f2f" : "text.disabled",
            fontSize: 10,
          }}
        >
          {text.length}/{MAX_LENGTH}
        </Typography>
      )}
    </Box>
  );
};
